import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';

/**
 * Idle-Time Prefetch Hook
 *
 * Pre-warms pages during browser idle time, but automatically yields
 * when the user requests another page. Uses requestIdleCallback which
 * the browser will pause/deprioritize when handling real user requests.
 *
 * @example
 * ```tsx
 * // In your layout or app component
 * useIdlePrefetch({
 *   routes: ['/dashboard', '/calls', '/device-setup'],
 *   delay: 2000, // Start after 2 seconds of idle
 * });
 * ```
 */

interface UseIdlePrefetchOptions {
  /** Routes to prefetch (in priority order) */
  routes: string[];
  /** Delay before starting prefetch (ms, default: 3000) */
  delay?: number;
  /** Whether prefetching is enabled (default: true) */
  enabled?: boolean;
  /** Time between route fetches (ms, default: 500) */
  staggerDelay?: number;
}

// Track globally which routes have been warmed this session
const warmedRoutes = new Set<string>();

export function useIdlePrefetch(options: UseIdlePrefetchOptions) {
  const {
    routes,
    delay = 3000,
    enabled = true,
    staggerDelay = 500,
  } = options;

  const router = useRouter();
  const abortControllerRef = useRef<AbortController | null>(null);
  const idleCallbackRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentIndexRef = useRef(0);
  const isPrefetchingRef = useRef(false);

  /**
   * Prefetch a single route using Next.js router prefetch
   * This warms the route's JS bundles and data
   */
  const prefetchRoute = useCallback(async (route: string): Promise<boolean> => {
    if (warmedRoutes.has(route)) {
      return true; // Already warmed
    }

    try {
      // Use Next.js built-in prefetch - this is non-blocking and
      // the browser will deprioritize it vs real navigation
      router.prefetch(route);

      // Also warm the API routes that page might need
      // Using fetch with low priority
      const warmUrl = `${route}${route.includes('?') ? '&' : '?'}_warm=1`;

      await fetch(warmUrl, {
        method: 'HEAD', // Just trigger compilation, don't need body
        signal: abortControllerRef.current?.signal,
        // @ts-ignore - priority hint for fetch
        priority: 'low',
      }).catch(() => {
        // Ignore fetch errors - page might not exist or need auth
        // The important part is triggering Next.js compilation
      });

      warmedRoutes.add(route);
      console.debug(`[IdlePrefetch] Warmed: ${route}`);
      return true;
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.debug(`[IdlePrefetch] Aborted: ${route} (user navigated)`);
        return false;
      }
      // Don't log other errors - route might need auth
      return false;
    }
  }, [router]);

  /**
   * Process the next route in the queue during idle time
   */
  const processNextRoute = useCallback(() => {
    if (!enabled || currentIndexRef.current >= routes.length) {
      isPrefetchingRef.current = false;
      return;
    }

    const route = routes[currentIndexRef.current];
    currentIndexRef.current++;

    prefetchRoute(route).then((success) => {
      if (success && currentIndexRef.current < routes.length) {
        // Schedule next route with stagger delay, but only during idle
        scheduleIdleWork();
      } else {
        isPrefetchingRef.current = false;
      }
    });
  }, [enabled, routes, prefetchRoute]);

  /**
   * Schedule work during browser idle time
   * requestIdleCallback automatically yields to user interactions
   */
  const scheduleIdleWork = useCallback(() => {
    if (typeof requestIdleCallback !== 'undefined') {
      // Clear any existing callback
      if (idleCallbackRef.current) {
        cancelIdleCallback(idleCallbackRef.current);
      }

      idleCallbackRef.current = requestIdleCallback(
        (deadline) => {
          // Only process if we have idle time remaining
          if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
            // Add stagger delay between routes
            timeoutRef.current = setTimeout(() => {
              processNextRoute();
            }, staggerDelay);
          } else {
            // No idle time, reschedule
            scheduleIdleWork();
          }
        },
        { timeout: 10000 } // Max 10s wait for idle time
      );
    } else {
      // Fallback for browsers without requestIdleCallback
      // Use setTimeout with longer delay
      timeoutRef.current = setTimeout(() => {
        processNextRoute();
      }, staggerDelay * 2);
    }
  }, [processNextRoute, staggerDelay]);

  /**
   * Start the prefetch process after initial delay
   */
  const startPrefetching = useCallback(() => {
    if (isPrefetchingRef.current || !enabled) return;

    isPrefetchingRef.current = true;
    abortControllerRef.current = new AbortController();
    currentIndexRef.current = 0;

    console.debug('[IdlePrefetch] Starting idle prefetch...', { routes: routes.length });
    scheduleIdleWork();
  }, [enabled, routes.length, scheduleIdleWork]);

  /**
   * Stop prefetching (e.g., when user navigates)
   */
  const stopPrefetching = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (idleCallbackRef.current) {
      cancelIdleCallback(idleCallbackRef.current);
      idleCallbackRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isPrefetchingRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Start prefetching after delay
    const startTimeout = setTimeout(() => {
      startPrefetching();
    }, delay);

    // Listen for user navigation - pause prefetching
    const handleRouteChange = () => {
      stopPrefetching();
      // Restart after settling on new page
      setTimeout(startPrefetching, delay);
    };

    // Pause on visibility change (user switched tabs)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPrefetching();
      } else {
        setTimeout(startPrefetching, delay);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(startTimeout);
      stopPrefetching();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, delay, startPrefetching, stopPrefetching]);

  return {
    /** Manually trigger prefetch of a specific route */
    prefetch: prefetchRoute,
    /** Stop all prefetching */
    stop: stopPrefetching,
    /** Restart prefetching */
    restart: startPrefetching,
    /** Routes that have been warmed */
    warmedRoutes: Array.from(warmedRoutes),
  };
}

/**
 * Get list of critical routes to prefetch
 * Ordered by likelihood of user visiting
 */
export function getCriticalRoutes(): string[] {
  return [
    '/dashboard',
    '/calls',
    '/incident-report',
    '/login',
    '/settings',
    '/knowledge-base',
    '/citizen',
    '/adminpanel',
  ];
}
